import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import LoadingAnimation from "../Loading/LoadingAnimation";
import "./EventManagments.css";
import EventService from "../../services/EventService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import Input from "../ui/input";
import TextArea from "../ui/textArea";

interface Events {
  IdEvent: number;
  EventName: string;
  Location: string;
  StartDate: Date;
  EndDate: Date;
  EventDescription: string;
  EventOverview: string;
}

const EventManagments: React.FC = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<Events[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [newEvent, setNewEvent] = useState({
    EventName: "",
    Location: "",
    StartDate: "",
    EndDate: "",
    EventDescription: "",
    EventOverview: "",
    EventBooth: "",
    IdCurrency: "",
  });
  const [showForm, setShowForm] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [curencies, setCurrencies] = useState<
    {
      IdCurrency: number;
      CurrencyCode: string;
      Currency: string;
    }[]
  >([]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setNewEvent({ ...newEvent, [name]: value });
  };

  useEffect(() => {
    document.title = "Events";
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await EventService.getEvents(0);
        setEvents(response.data.My_Result);
        const currenciesResponse = await EventService.getCurrencies();
        setCurrencies(currenciesResponse.data.My_Result);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!showForm) {
      setNewEvent({
        EventName: "",
        Location: "",
        StartDate: "",
        EndDate: "",
        EventDescription: "",
        EventOverview: "",
        EventBooth: "",
        IdCurrency: "",
      });
    }
  }, [showForm]);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await EventService.createEvent(newEvent);
      if (response.status === 200) {
        setSnackbarOpen(false);
        setShowForm(false);
        const eventsResponse = await EventService.getEvents(0);
        setEvents(eventsResponse.data.My_Result);
      } else {
        setMessage("Failed to create event");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error creating event:", error);
      setMessage("Error creating event");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (event: any) => {
    window.location.href = `/event-managments/${event.IdEvent}`;
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value.toLowerCase());
  };

  const filteredEvents = Array.isArray(events)
    ? events.filter(
        (event) =>
          event.EventName.toLowerCase().includes(searchTerm) ||
          event.Location.toLowerCase().includes(searchTerm)
      )
    : [];

  return (
    <div className="event-management">
      <div className="events-header">
        <h1>Events</h1>
        <button onClick={() => setShowForm(true)}>Create event</button>
      </div>

      {showForm && (
        <div className="modal">
          <div className="bg-white w-96 max-w-full rounded-2xl p-5 relative">
            <button className="modal-close" onClick={() => setShowForm(false)}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <form
              className="create-event-form space-y-2"
              onSubmit={handleFormSubmit}>
              <label>Event Name:</label>
              <Input
                id="EventName"
                type="text"
                name="EventName"
                placeholder="Event Title"
                value={newEvent.EventName}
                onChange={handleInputChange}
                required
              />
              <label>Location:</label>
              <Input
                id="Location"
                type="text"
                name="Location"
                placeholder="Event Location"
                value={newEvent.Location}
                onChange={handleInputChange}
                required
              />
              <div className="flex">
                <div className="flex-1">
                  <label>Start Date:</label>
                  <Input
                    id="StartDate"
                    type="date"
                    name="StartDate"
                    value={newEvent.StartDate}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="flex-1">
                  <label>End Date:</label>
                  <Input
                    id="EndDate"
                    type="date"
                    name="EndDate"
                    value={newEvent.EndDate}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <label>Event Booth:</label>
              <Input
                id="EventBooth"
                type="text"
                name="EventBooth"
                placeholder="Event Booth"
                value={newEvent.EventBooth}
                onChange={handleInputChange}
                required
              />
              <label>Event Currency:</label>
              <select
                name="IdCurrency"
                className="bg-white border border-gray-300 rounded-2xl py-2 px-3 h-10"
                onChange={(e) => {
                  const target = e.target as HTMLSelectElement;
                  setNewEvent({ ...newEvent, IdCurrency: target.value });
                }}>
                {curencies.map((currency, index) => (
                  <option key={index} value={currency.IdCurrency}>
                    {currency.CurrencyCode} {currency.Currency}
                  </option>
                ))}
              </select>

              <label>Event Description:</label>
              <TextArea
                id="EventDescription"
                name="EventDescription"
                placeholder="Event Description"
                value={newEvent.EventDescription}
                onChange={handleInputChange}
                required
              />
              <label>Event Overview:</label>
              <TextArea
                id="EventOverview"
                name="EventOverview"
                placeholder="Event Overview"
                value={newEvent.EventOverview}
                onChange={handleInputChange}
                required
              />

              <button
                type="submit"
                disabled={loading}
                className="add-event-button rounded-2xl">
                {loading ? <LoadingAnimation /> : "Add Event"}
              </button>
            </form>
          </div>
        </div>
      )}

      <div className="search-bar">
        <input
          type="text"
          placeholder="Search events"
          value={searchTerm}
          onChange={handleSearch}
        />
      </div>

      <div className="events-grid">
        {filteredEvents.length > 0 ? (
          filteredEvents.map((event) => (
            <div
              key={event.IdEvent}
              className="event-card cursor-pointer"
              onClick={() => handleCardClick(event)}>
              <h3>{event.EventName}</h3>
              <p>{event.Location}</p>
              <p>Start Date: {new Date(event.StartDate).toDateString()}</p>
              <p>End Date: {new Date(event.EndDate).toDateString()}</p>
            </div>
          ))
        ) : (
          <LoadingAnimation />
        )}
      </div>

      {snackbarOpen && (
        <div className="snackbar">
          <p>{message || "Error: Start date cannot be later than end date."}</p>
        </div>
      )}
    </div>
  );
};

export default EventManagments;
